import { useState } from 'react'

import { useQuoteI18n } from '../quoteI18n'
import { TagBadge } from './TagBadge'

/**
 * Una singola offerta, selezionabile.
 *
 * Non prenota da sola: la scelta di un verticale entra nel carrello e la prenotazione parte
 * una volta sola, alla fine, con tutti i servizi scelti. Vedi `SelectionSummary`.
 *
 * @param {Object} props
 * @param {Object} props.offer - Offerta normalizzata
 * @param {boolean} props.selected - True se è la scelta corrente per il suo verticale
 * @param {(offer: Object) => void} props.onToggle - Seleziona/deseleziona questa offerta
 */
export function OfferCard({ offer, selected, onToggle }) {
  const { t } = useQuoteI18n()
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="gq-offer" data-selected={selected}>
      <div className="gq-offer__main">
        {!!offer.tags?.length && (
          <div className="gq-offer__tags">
            {offer.tags.map((tag) => (
              <TagBadge key={tag} tag={tag} />
            ))}
          </div>
        )}
        <div className="gq-offer__summary">{offer.summary}</div>
        <OfferGlance offer={offer} />
        {expanded && <OfferExtra offer={offer} />}
        <button
          type="button"
          className="gq-offer__details-toggle"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
        >
          {expanded ? t.offer.lessDetails : t.offer.moreDetails}
        </button>
      </div>

      <div className="gq-offer__aside">
        <div className="gq-offer__price">
          {formatPrice(offer.price, offer.currencyCode)}
          <PriceCoverage offer={offer} />
        </div>
        <button
          type="button"
          className={`gq-btn ${selected ? 'gq-btn--primary' : 'gq-btn--ghost'}`}
          onClick={() => onToggle(offer)}
          aria-pressed={selected}
          aria-label={
            selected ? t.offer.deselectAria(offer.summary) : t.offer.selectAria(offer.summary)
          }
        >
          {selected ? t.offer.selected : t.offer.select}
        </button>
      </div>
    </div>
  )
}

/**
 * Cosa copre il prezzo grande, quando non è ovvio.
 *
 * Per l'auto Goelett quota al giorno e la normalizzazione moltiplica per i giorni di
 * noleggio: senza dirlo, lo stesso carattere che sull'hotel è un totale di soggiorno
 * sull'auto sarebbe un numero senza unità. I preventivi generati prima dell'introduzione
 * di `rentalDays` non l'hanno e restano senza riga, che è il comportamento precedente.
 */
function PriceCoverage({ offer }) {
  const { t } = useQuoteI18n()
  const days = offer.segmentType === 'Car' ? offer.details?.rentalDays : undefined
  if (!days) return null
  return <div className="gq-offer__price-unit">{t.offer.days(days)}</div>
}

// ======================= A colpo d'occhio =======================

/**
 * Informazioni essenziali visibili subito: rotta+orari per trasporti,
 * città+stelle per hotel, categoria+ritiro per auto.
 */
function OfferGlance({ offer }) {
  const { segmentType } = offer
  if (segmentType === 'Air') return <AirGlance offer={offer} />
  if (segmentType === 'Rail') return <RailGlance offer={offer} />
  if (segmentType === 'Hotel') return <HotelGlance offer={offer} />
  if (segmentType === 'Car') return <CarGlance offer={offer} />
  return null
}

function AirGlance({ offer }) {
  const { t, locale } = useQuoteI18n()
  const { details } = offer
  const outbound = details?.outboundLeg
  const ret = details?.returnLeg
  return (
    <div className="gq-offer__details">
      {outbound && (
        <AirLegRow leg={outbound} label={ret ? t.offer.outbound : undefined} locale={locale} />
      )}
      {ret && <AirLegRow leg={ret} label={t.offer.return} locale={locale} />}
    </div>
  )
}

function AirLegRow({ leg, label, locale }) {
  const dep = leg?.blocks?.[0]
  const arr = leg?.blocks?.[leg?.blocks?.length - 1]
  const route =
    dep?.departureAirportCode &&
    arr?.arrivalAirportCode &&
    `${dep.departureAirportCode} → ${arr.arrivalAirportCode}`
  const depTime = formatTime(dep?.departureDate)
  const arrTime = formatTime(arr?.arrivalDate)
  const date = formatShortDate(dep?.departureDate, locale)

  return (
    <div className="gq-offer__leg-row">
      {label && <span className="gq-offer__leg-label">{label}</span>}
      <span className="gq-offer__leg-route">{route}</span>
      {depTime && arrTime && (
        <span className="gq-offer__leg-times">
          {depTime}–{arrTime}
        </span>
      )}
      {date && <span className="gq-offer__leg-date">{date}</span>}
      {leg?.duration > 0 && (
        <span className="gq-offer__leg-duration">{formatDuration(leg.duration)}</span>
      )}
    </div>
  )
}

function RailGlance({ offer }) {
  const { t, locale } = useQuoteI18n()
  const { details } = offer
  const blocks = details?.blocks ?? []
  const dep = blocks[0]
  const arr = blocks[blocks.length - 1] ?? dep
  const direction = details?.direction
  const dirLabel = direction === 'return' ? t.offer.return : t.offer.outbound
  const changes = blocks.length > 1 ? blocks.length - 1 : 0

  return (
    <div className="gq-offer__details">
      <div className="gq-offer__leg-row">
        <span className="gq-offer__leg-label">{dirLabel}</span>
        <span className="gq-offer__leg-route">
          {dep?.departureRailwayStationName ?? ''} → {arr?.arrivalRailwayStationName ?? ''}
        </span>
        {dep?.departureDate && (
          <span className="gq-offer__leg-times">
            {formatTime(dep.departureDate)}–{formatTime(arr?.arrivalDate)}
          </span>
        )}
        {dep?.departureDate && (
          <span className="gq-offer__leg-date">{formatShortDate(dep.departureDate, locale)}</span>
        )}
        {details?.duration > 0 && (
          <span className="gq-offer__leg-duration">{formatDuration(details.duration)}</span>
        )}
        {changes > 0 && (
          <span className="gq-offer__leg-changes">
            {changes} {changes === 1 ? t.offer.change : t.offer.changes}
          </span>
        )}
      </div>
    </div>
  )
}

function HotelGlance({ offer }) {
  const { t, locale } = useQuoteI18n()
  const { details } = offer
  // `distanceFromAnchorKm` è la distanza dal punto che il viaggiatore ha indicato (zona,
  // landmark, indirizzo), calcolata da noi; `distanceFromSearchPoint` è quella dal centro
  // ricerca, che arriva da Goelett. Sono due misure diverse e vanno dette con due frasi
  // diverse, altrimenti la prossimità a un monumento viene letta come distanza dal centro.
  const anchorKm = details?.distanceFromAnchorKm
  const distance = anchorKm ?? details?.distanceFromSearchPoint
  const distanceText =
    distance == null
      ? ''
      : anchorKm != null
        ? t.offer.distanceFromAnchor(formatKm(anchorKm, locale))
        : t.offer.distanceFromSearch(formatKm(distance, locale))

  return (
    <div className="gq-offer__details">
      {joinParts([
        details?.cityName,
        details?.comfort && '★'.repeat(details.comfort),
        details?.roomName,
        details?.mealType
      ])}
      {distanceText && <span className="gq-offer__detail-secondary">&nbsp;{distanceText}</span>}
    </div>
  )
}

function CarGlance({ offer }) {
  const { t } = useQuoteI18n()
  const { details, currencyCode } = offer
  const perDay = details?.pricePerDay
  const days = details?.rentalDays
  // Il prezzo grande della card è il totale: qui si mostra da cosa deriva, altrimenti il
  // giornaliero pubblicato dal fornitore non si ritrova da nessuna parte.
  const priceText =
    perDay == null
      ? ''
      : days
        ? `${formatPrice(perDay, currencyCode)} / ${t.offer.perDayShort} · ${t.offer.days(days)}`
        : `${formatPrice(perDay, currencyCode)} ${t.offer.perDay}`

  const pickup = textOf(details?.pickupStation)
  const dropoff = textOf(details?.returnStation)

  return (
    <div className="gq-offer__details">
      {joinParts([details?.categoryName, details?.transmissionType, details?.fuelType, priceText])}
      {pickup && (
        <div className="gq-offer__detail-secondary">
          {t.offer.pickup}: {pickup}
          {dropoff && dropoff !== pickup && ` → ${dropoff}`}
        </div>
      )}
    </div>
  )
}

// ======================= Dettagli extra (toggle) =======================

/**
 * Pannello dettagli, aperto dal toggle "Dettagli".
 *
 * Tre livelli, nell'ordine in cui servono a decidere:
 * 1. i **segnali** che cambiano la natura dell'offerta e non si leggono dal prezzo
 *    (biglietti separati, cabina mista, struttura preferita, tasse escluse dal totale);
 * 2. l'**itinerario** dei trasporti come timeline, con vettore, numero, classe e la durata
 *    degli scali — il tempo che il viaggiatore perde davvero, che non arriva come campo;
 * 3. le **condizioni** e le specifiche, in sezioni etichettate.
 */
function OfferExtra({ offer }) {
  const { segmentType } = offer
  if (segmentType === 'Air') return <AirExtra offer={offer} />
  if (segmentType === 'Rail') return <RailExtra offer={offer} />
  if (segmentType === 'Hotel') return <HotelExtra offer={offer} />
  if (segmentType === 'Car') return <CarExtra offer={offer} />
  return null
}

// ----------------------- Aereo -----------------------

function AirExtra({ offer }) {
  const { t } = useQuoteI18n()
  const { details } = offer
  const outbound = details?.outboundLeg
  const ret = details?.returnLeg
  const mixedCabin = hasMixedCabin(outbound) || hasMixedCabin(ret)
  const legsDiffer = !!ret && !sameFare(outbound, ret)

  return (
    <div className="gq-offer__extra">
      <Signals
        items={[
          details?.isMultiTicket && {
            kind: 'warning',
            label: t.offer.separateTickets,
            hint: t.offer.separateTicketsHint
          },
          mixedCabin && {
            kind: 'warning',
            label: t.offer.mixedCabin,
            hint: t.offer.mixedCabinHint
          },
          details?.isLowCost && { kind: 'info', label: t.offer.lowCost }
        ]}
      />

      <SpecSection title={t.offer.sectionItinerary} show={!!(outbound || ret)}>
        {outbound && <AirLegTimeline leg={outbound} label={ret ? t.offer.outbound : undefined} />}
        {ret && <AirLegTimeline leg={ret} label={t.offer.return} />}
      </SpecSection>

      <SpecSection
        title={t.offer.sectionFare}
        rows={[
          ...legFareRows(outbound, legsDiffer ? t.offer.outbound : undefined, t),
          ...(legsDiffer ? legFareRows(ret, t.offer.return, t) : []),
          {
            label: t.offer.refundableLabel,
            value: details?.isRefundable ? t.offer.refundable : t.offer.nonRefundable
          },
          {
            label: t.offer.exchangeableLabel,
            value: details?.isExchangeable ? t.offer.yes : t.offer.no
          },
          {
            label: t.offer.totalDuration,
            value: details?.duration > 0 ? formatDuration(details.duration) : undefined
          }
        ]}
      />
    </div>
  )
}

/**
 * Cabina, tariffa e vettore di una tratta.
 *
 * L'etichetta di direzione compare solo quando andata e ritorno differiscono: ripeterla su
 * un volo che ha la stessa tariffa nei due sensi raddoppierebbe le righe senza aggiungere
 * niente.
 */
function legFareRows(leg, prefix, t) {
  if (!leg) return []
  const at = (label) => (prefix ? `${prefix} — ${label}` : label)
  return [
    { label: at(t.offer.cabin), value: leg.cabinClass },
    { label: at(t.offer.fare), value: leg.fareFamilyName },
    { label: at(t.offer.carrier), value: leg.carrierName }
  ]
}

/**
 * Timeline di una tratta aerea: un nodo per aeroporto, un segmento per volo.
 *
 * Il segmento porta numero di volo, vettore marketing e — solo quando differisce — quello
 * operativo, perché su un code-share si sale sull'aereo di una compagnia diversa da quella
 * che ha venduto il biglietto. Fra due voli si mostra la durata dello scalo, calcolata
 * dagli orari: è ciò che distingue una coincidenza comoda da una da evitare.
 */
function AirLegTimeline({ leg, label }) {
  const { t, locale } = useQuoteI18n()
  const blocks = leg?.blocks ?? []
  if (!blocks.length) return null

  return (
    <div className="gq-tl">
      {label && <div className="gq-tl__label">{label}</div>}
      {blocks.map((block, i) => {
        const next = blocks[i + 1]
        const layover = next ? minutesBetween(block.arrivalDate, next.departureDate) : 0
        return (
          <div key={i} className="gq-tl__block">
            <TimelineStop
              time={formatTime(block.departureDate)}
              date={i === 0 ? formatShortDate(block.departureDate, locale) : undefined}
              code={block.departureAirportCode}
              place={block.departureAirportName || block.departureCityName}
            />
            <div className="gq-tl__leg">
              {joinParts([
                block.flightNumber,
                block.marketingCarrierName,
                block.operatingCarrierName &&
                  block.operatingCarrierName !== block.marketingCarrierName &&
                  t.offer.operatedBy(block.operatingCarrierName),
                block.cabinClass,
                block.duration > 0 && formatDuration(block.duration)
              ])}
            </div>
            <TimelineStop
              time={formatTime(block.arrivalDate)}
              code={block.arrivalAirportCode}
              place={block.arrivalAirportName || block.arrivalCityName}
            />
            {layover > 0 && (
              <div className="gq-tl__wait">{t.offer.layover(formatDuration(layover))}</div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ----------------------- Treno -----------------------

function RailExtra({ offer }) {
  const { t } = useQuoteI18n()
  const { details } = offer
  const blocks = details?.blocks ?? []

  return (
    <div className="gq-offer__extra">
      <SpecSection title={t.offer.sectionItinerary} show={!!blocks.length}>
        <RailTimeline blocks={blocks} />
      </SpecSection>

      <SpecSection
        title={t.offer.sectionFare}
        rows={[
          { label: t.offer.cabin, value: details?.cabinClassType },
          { label: t.offer.fare, value: details?.fareName },
          { label: t.offer.provider, value: details?.providerName },
          {
            label: t.offer.refundableLabel,
            value: details?.isRefundable ? t.offer.refundable : t.offer.nonRefundable
          },
          {
            label: t.offer.exchangeableLabel,
            value: details?.isExchangeable ? t.offer.yes : t.offer.no
          },
          { label: t.offer.cancellationPolicy, value: details?.cancellationPolicy },
          {
            label: t.offer.totalDuration,
            value: details?.duration > 0 ? formatDuration(details.duration) : undefined
          }
        ]}
      />
    </div>
  )
}

/**
 * Timeline di un viaggio in treno. Rispetto all'aereo il segmento porta tipo e numero del
 * convoglio (TGV 9241) e il wifi a bordo, e fra due tratte si nomina la stazione di cambio:
 * su ferro il cambio è un luogo, non solo un'attesa.
 */
function RailTimeline({ blocks }) {
  const { t, locale } = useQuoteI18n()
  return (
    <div className="gq-tl">
      {blocks.map((block, i) => {
        const next = blocks[i + 1]
        const wait = next ? minutesBetween(block.arrivalDate, next.departureDate) : 0
        return (
          <div key={i} className="gq-tl__block">
            <TimelineStop
              time={formatTime(block.departureDate)}
              date={i === 0 ? formatShortDate(block.departureDate, locale) : undefined}
              place={block.departureRailwayStationName || block.departureCityName}
            />
            <div className="gq-tl__leg">
              {joinParts([
                joinSpace([block.trainType, block.trainNumber]),
                block.carrierName,
                block.duration > 0 && formatDuration(block.duration),
                block.wifiServiceAvailable && t.offer.wifiOnBoard
              ])}
            </div>
            <TimelineStop
              time={formatTime(block.arrivalDate)}
              place={block.arrivalRailwayStationName || block.arrivalCityName}
            />
            {next && (
              <div className="gq-tl__wait">
                {joinParts([
                  joinSpace([t.offer.changeAt, next.departureRailwayStationName]),
                  wait > 0 && formatDuration(wait)
                ])}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ----------------------- Hotel -----------------------

function HotelExtra({ offer }) {
  const { t } = useQuoteI18n()
  const { details } = offer
  const amenities = (details?.amenities ?? []).filter(Boolean)
  const breakdown = details?.ratingBreakdown
  const hasMap = details?.latitude != null && details?.longitude != null

  return (
    <div className="gq-offer__extra">
      <Signals
        items={[
          details?.isFavouriteHotel && { kind: 'success', label: t.offer.favourite },
          details?.isRoomForDisabled && { kind: 'info', label: t.offer.accessibleRoom },
          details?.localTaxInfo && {
            kind: 'warning',
            label: t.offer.localTax,
            hint: t.offer.localTaxHint
          }
        ]}
      />

      <SpecSection
        title={t.offer.sectionProperty}
        show={!!breakdown || hasMap}
        rows={[
          { label: t.offer.chain, value: details?.hotelGroupName },
          { label: t.offer.address, value: details?.hotelAddress },
          {
            label: t.offer.ratingLabel,
            value:
              details?.rating == null
                ? undefined
                : joinParts([
                    `${details.rating}/10`,
                    details?.reviewCount > 0 && t.offer.reviews(details.reviewCount)
                  ])
          }
        ]}
      >
        <RatingBreakdown breakdown={breakdown} />
        {hasMap && <HotelMap latitude={details.latitude} longitude={details.longitude} />}
      </SpecSection>

      <SpecSection
        title={t.offer.sectionRoom}
        rows={[
          { label: t.offer.roomType, value: details?.roomTypeName ?? details?.roomName },
          { label: t.offer.mealPlan, value: details?.mealType },
          { label: t.offer.refundableLabel, value: details?.refundability },
          { label: t.offer.localTax, value: details?.localTaxInfo }
        ]}
      />

      <SpecSection title={t.offer.sectionServices} show={!!amenities.length}>
        <Chips items={amenities} />
      </SpecSection>
    </div>
  )
}

/**
 * Rating per categoria come barre su scala 0-10.
 *
 * Le categorie che il fornitore non valorizza non arrivano nemmeno: `ratingBreakdownOf`
 * lato Executor le scarta, perché una barra vuota comunica «scadente», non «non pervenuto».
 */
function RatingBreakdown({ breakdown }) {
  const { t } = useQuoteI18n()
  const entries = Object.entries(breakdown ?? {})
  if (!entries.length) return null
  const labelOf = {
    cleanliness: t.offer.ratingCleanliness,
    staffService: t.offer.ratingStaffService,
    amenities: t.offer.ratingAmenities,
    propertyConditions: t.offer.ratingPropertyConditions
  }
  return (
    <div className="gq-rating">
      {entries.map(([key, value]) => (
        <div className="gq-rating__row" key={key}>
          <span className="gq-rating__label">{labelOf[key] ?? key}</span>
          <span className="gq-rating__track">
            <span className="gq-rating__fill" style={{ width: `${clampPercent(value * 10)}%` }} />
          </span>
          <span className="gq-rating__value">{value}</span>
        </div>
      ))}
    </div>
  )
}

// ----------------------- Auto -----------------------

function CarExtra({ offer }) {
  const { t, locale } = useQuoteI18n()
  const { details, price, currencyCode } = offer
  const equipment = (details?.equipmentList ?? []).filter(Boolean)
  const pickup = details?.pickupStation
  const dropoff = details?.returnStation
  const sameStation = textOf(dropoff) === textOf(pickup)
  const perDay = details?.pricePerDay
  const days = details?.rentalDays
  const perDayText =
    perDay == null ? undefined : `${formatPrice(perDay, currencyCode)} / ${t.offer.perDayShort}`

  return (
    <div className="gq-offer__extra">
      <SpecSection
        title={t.offer.sectionVehicle}
        show={!!equipment.length}
        rows={[
          { label: t.offer.rentalCompany, value: details?.rentalCompanyName },
          { label: t.offer.transmission, value: details?.transmissionType },
          { label: t.offer.fuel, value: details?.fuelType },
          {
            label: t.offer.airConditioning,
            value:
              details?.isAirConditioning == null
                ? undefined
                : details.isAirConditioning
                  ? t.offer.yes
                  : t.offer.no
          }
        ]}
      >
        <Chips items={equipment} />
      </SpecSection>

      <SpecSection title={t.offer.sectionStations} show={!!textOf(pickup)}>
        <StationRow label={t.offer.pickupStation} station={pickup} />
        {!sameStation && <StationRow label={t.offer.returnStation} station={dropoff} />}
        {details?.distanceFromSearchPoint != null && (
          <span className="gq-offer__extra-sub">
            {t.offer.distanceFromSearch(formatKm(details.distanceFromSearchPoint, locale))}
          </span>
        )}
      </SpecSection>

      <SpecSection
        title={t.offer.sectionPrice}
        rows={[
          {
            label: t.offer.estimatedTotal,
            value:
              perDayText && days
                ? `${t.offer.priceBreakdown(perDayText, t.offer.days(days))} = ${formatPrice(price, currencyCode)}`
                : perDayText
          }
        ]}
      />
    </div>
  )
}

/** Stazione di noleggio: nome sulla riga, indirizzo sotto quando c'è. */
function StationRow({ label, station }) {
  const name = textOf(station)
  if (!name) return null
  const address = typeof station === 'object' ? station?.address : ''
  return (
    <div className="gq-offer__extra-row">
      <span className="gq-offer__extra-label">{label}</span>
      <span>
        {name}
        {address && <span className="gq-offer__extra-sub">{address}</span>}
      </span>
    </div>
  )
}

// ======================= Mattoni del pannello =======================

/**
 * Gruppo di righe con un titolo, che dà al pannello una gerarchia invece di un elenco piatto.
 *
 * Le righe arrivano come **dati** e non come JSX di proposito: un elemento React è truthy
 * anche quando il componente che rappresenta renderà `null`, quindi contando i children non
 * si può sapere se la sezione è vuota, e un titolo su zero righe finirebbe a video. Per il
 * contenuto che non è una riga (timeline, chip, barre) il chiamante dichiara `show`, perché
 * la condizione la conosce dai dati.
 *
 * @param {Object} props
 * @param {string} props.title - Titolo della sezione
 * @param {Array<{label: string, value: any}>} [props.rows] - Righe etichetta/valore
 * @param {boolean} [props.show] - Forza la sezione quando il contenuto non è fatto di righe
 */
function SpecSection({ title, rows = [], show = false, children }) {
  const visible = rows.filter((row) => hasValue(row.value))
  if (!visible.length && !show) return null
  return (
    <div className="gq-spec">
      <div className="gq-spec__title">{title}</div>
      {visible.map((row) => (
        <SpecRow key={row.label} label={row.label} value={row.value} />
      ))}
      {children}
    </div>
  )
}

function SpecRow({ label, value }) {
  if (!hasValue(value)) return null
  return (
    <div className="gq-offer__extra-row">
      <span className="gq-offer__extra-label">{label}</span>
      <span>{value}</span>
    </div>
  )
}

/**
 * Segnali in evidenza: quello che cambia la natura dell'offerta e non si legge dal prezzo.
 * Gli elementi falsi vengono scartati, così il chiamante può scriverli in linea con `&&`.
 */
function Signals({ items }) {
  const visible = items.filter(Boolean)
  if (!visible.length) return null
  return (
    <div className="gq-signals">
      {visible.map(({ kind, label, hint }) => (
        <span className="gq-signal" data-kind={kind} key={label}>
          <span className="gq-signal__label">{label}</span>
          {hint && <span className="gq-signal__hint">{hint}</span>}
        </span>
      ))}
    </div>
  )
}

function Chips({ items }) {
  const visible = items.filter(Boolean)
  if (!visible.length) return null
  return (
    <div className="gq-chips">
      {visible.map((item, i) => (
        <span className="gq-chip" key={`${item}-${i}`}>
          {item}
        </span>
      ))}
    </div>
  )
}

/** Nodo della timeline: ora, pallino, luogo (con codice IATA quando c'è) ed eventuale data. */
function TimelineStop({ time, date, code, place }) {
  return (
    <div className="gq-tl__stop">
      <span className="gq-tl__time">{time}</span>
      <span className="gq-tl__dot" aria-hidden />
      <span className="gq-tl__place">
        {code && <span className="gq-tl__code">{code}</span>}
        {place}
      </span>
      {date && <span className="gq-tl__date">{date}</span>}
    </div>
  )
}

// ======================= Helpers =======================

/** `true` se le tratte del volo non hanno tutte la stessa cabina (itinerario a cabina mista). */
function hasMixedCabin(leg) {
  const cabins = (leg?.blocks ?? []).map((b) => b?.cabinClass).filter(Boolean)
  return new Set(cabins).size > 1
}

/** Due tratte hanno la stessa tariffa? Se sì, cabina/tariffa/vettore si mostrano una volta. */
function sameFare(a, b) {
  if (!a || !b) return false
  return (
    a.cabinClass === b.cabinClass &&
    a.fareFamilyName === b.fareFamilyName &&
    a.carrierName === b.carrierName
  )
}

/** Minuti fra due ISO date-time; 0 se una delle due manca o l'ordine è invertito. */
function minutesBetween(fromIso, toIso) {
  if (!fromIso || !toIso) return 0
  const diff = new Date(toIso).getTime() - new Date(fromIso).getTime()
  if (!Number.isFinite(diff) || diff <= 0) return 0
  return Math.round(diff / 60000)
}

function hasValue(value) {
  return value != null && value !== ''
}

function clampPercent(value) {
  return Math.max(0, Math.min(100, value))
}

/** Estrae testo leggibile da un valore che può essere stringa o oggetto con `.name`. */
function textOf(value) {
  if (!value) return ''
  if (typeof value === 'string') return value
  return value.name || value.code || ''
}

function joinParts(parts) {
  return parts.filter(Boolean).join(' · ')
}

function joinSpace(parts) {
  return parts.filter(Boolean).join(' ')
}

function formatPrice(price, currencyCode) {
  const symbol = currencyCode === 'EUR' ? '€' : currencyCode || ''
  return `${symbol} ${(price ?? 0).toFixed(2)}`
}

function formatKm(value, locale) {
  return value.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
}

function formatDuration(minutes) {
  if (!minutes) return ''
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return h > 0 ? `${h}h${m > 0 ? `${m}m` : ''}` : `${m}m`
}

function formatTime(isoString) {
  if (!isoString) return ''
  try {
    const d = new Date(isoString)
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  } catch {
    return ''
  }
}

function formatShortDate(isoString, locale) {
  if (!isoString) return ''
  try {
    return new Date(isoString).toLocaleDateString(locale, { day: '2-digit', month: 'short' })
  } catch {
    return ''
  }
}

/**
 * Mappa OpenStreetMap embedded per la posizione dell'hotel.
 * Nessun API key necessario: usa l'export embed di OSM.
 */
function HotelMap({ latitude, longitude }) {
  const delta = 0.005
  const bbox = `${longitude - delta},${latitude - delta},${longitude + delta},${latitude + delta}`
  const src = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${latitude},${longitude}`
  return (
    <div className="gq-offer__map">
      <iframe title="Hotel map" src={src} loading="lazy" referrerPolicy="no-referrer" />
    </div>
  )
}
